import { useHookstate } from "@hookstate/core";
import { useCallback, useMemo } from "react";
import {
  Artist,
  Bundle,
  BundlePurchasePayload,
  Comic,
  Release,
  ServerListResponse,
  ServerResponse,
  ServerSingleResponse,
  User,
} from "../../../types";
import { apiClient } from "../../../services/axiosInstance";
import { ReaderNavigationContext } from "../../../screens/reader/types";
import { SLIDE_IN_MENUS } from "../../../screens/root/constants";
import { rootComponentsStore } from "../../../hookstate/rootComponentsStore";
import { useIsInReader } from "../../../hooks/useIsInReader";

function createMenuInstanceId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `menu_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
}

export function useSlideInMenu() {
  // Subscribe to openMenu and individual config properties
  const openMenuState = useHookstate(rootComponentsStore.openSlideInMenu);
  const openMenu = openMenuState.get();

  // Subscribe to individual config properties to get reactivity
  const menuConfigStateSubscribed = useHookstate(
    rootComponentsStore.slideInMenuConfig,
  );
  const releaseUuid = menuConfigStateSubscribed.releaseUuid?.get();
  const comicSlug = menuConfigStateSubscribed.comicSlug?.get();
  const menuInstanceId = menuConfigStateSubscribed.menuInstanceId?.get();
  const artistId = menuConfigStateSubscribed.artistId?.get();
  const username = menuConfigStateSubscribed.username?.get();
  const bundleId = menuConfigStateSubscribed.bundleId?.get();
  const navigationContext = menuConfigStateSubscribed.navigationContext?.get({
    noproxy: true,
  });

  // Don't subscribe to history - just reference directly
  const menuConfigState = rootComponentsStore.slideInMenuConfig;
  const slideInMenuHistoryState = rootComponentsStore.slideInMenuHistory;

  const inReader = useIsInReader();

  const onCloseMenu = useCallback(() => {
    requestAnimationFrame(() => {
      openMenuState.set(undefined);
      menuConfigState.set({});
      slideInMenuHistoryState.set([]);
    });
  }, [menuConfigState, openMenuState, slideInMenuHistoryState]);

  const hasMenuConfigChanged = useCallback(
    (newConfig?: {
      releaseUuid?: string;
      comicSlug?: string;
      artistId?: number;
      username?: string;
      bundleId?: string;
      navigationContext?: ReaderNavigationContext;
    }): boolean => {
      if (!newConfig) return false;

      const currentConfig = menuConfigState.get({ noproxy: true });

      return (
        newConfig.releaseUuid !== currentConfig.releaseUuid ||
        newConfig.comicSlug !== currentConfig.comicSlug ||
        newConfig.artistId !== currentConfig.artistId ||
        newConfig.username !== currentConfig.username ||
        newConfig.bundleId !== currentConfig.bundleId
      );
    },
    [menuConfigState],
  );

  const onToggleSlideInMenu = useCallback(
    (
      menu: SLIDE_IN_MENUS,
      newConfig?: {
        releaseUuid?: string;
        comicSlug?: string;
        artistId?: number;
        username?: string;
        bundleId?: string;
        navigationContext?: ReaderNavigationContext;
      },
      noHistoy?: boolean,
    ) => {
      const openMenu = openMenuState.get();
      const isConfigChanging = hasMenuConfigChanged(newConfig);

      // Only close if same menu AND in reader AND config is NOT changing
      if (openMenu === menu && inReader && !isConfigChanging) {
        onCloseMenu();
      } else {
        if (inReader) rootComponentsStore.isSidebarOpen.set(false);

        if (openMenu && !noHistoy) {
          const previousConfig = {
            type: openMenu,
            config: { ...menuConfigState.get({ noproxy: true }) },
          };
          const currentHistory = slideInMenuHistoryState.get({ noproxy: true });
          slideInMenuHistoryState.set([previousConfig, ...currentHistory]);
        }

        openMenuState.set(menu);
        menuConfigState.set({
          ...newConfig,
          menuInstanceId: createMenuInstanceId(),
        });
      }
    },
    [
      menuConfigState,
      openMenuState,
      inReader,
      onCloseMenu,
      hasMenuConfigChanged,
      slideInMenuHistoryState,
    ],
  );

  const goBack = useCallback(() => {
    const currentHistory = slideInMenuHistoryState.get({ noproxy: true });
    if (currentHistory.length > 0) {
      const last = currentHistory[0];
      openMenuState.set(last.type);
      menuConfigState.set({ ...last.config });
      slideInMenuHistoryState.set(currentHistory.slice(1));
    }
  }, [menuConfigState, openMenuState, slideInMenuHistoryState]);

  const openReleaseInfoMenu = useCallback(
    (
      releaseUuid: string,
      comicSlug?: string,
      navigationContext?: ReaderNavigationContext,
      noHistory?: boolean,
    ) => {
      onToggleSlideInMenu(
        SLIDE_IN_MENUS.releaseInfo,
        {
          releaseUuid,
          comicSlug,
          navigationContext,
        },
        noHistory,
      );
    },
    [onToggleSlideInMenu],
  );

  const openComicInfoMenu = useCallback(
    (
      comicSlug: string,
      releaseUuid?: string,
      navigationContext?: ReaderNavigationContext,
    ) => {
      onToggleSlideInMenu(SLIDE_IN_MENUS.comicDetails, {
        releaseUuid,
        comicSlug,
        navigationContext,
      });
    },
    [onToggleSlideInMenu],
  );

  const openArtistProfile = useCallback(
    (id: number, navigationContext?: ReaderNavigationContext) => {
      onToggleSlideInMenu(SLIDE_IN_MENUS.publisher, {
        artistId: id,
        navigationContext,
      });
    },
    [onToggleSlideInMenu],
  );

  const openUserProfile = useCallback(
    (username: string, navigationContext?: ReaderNavigationContext) => {
      onToggleSlideInMenu(SLIDE_IN_MENUS.userProfile, {
        username,
        navigationContext,
      });
    },
    [onToggleSlideInMenu],
  );

  const historyLength = slideInMenuHistoryState.get({ noproxy: true }).length;

  const openBundleDetails = useCallback(
    (bundleId: string, navigationContext?: ReaderNavigationContext) => {
      onToggleSlideInMenu(SLIDE_IN_MENUS.bundleDetails, {
        bundleId: bundleId,
        navigationContext,
      });
    },
    [onToggleSlideInMenu],
  );

  return useMemo(
    () => ({
      openComicInfoMenu,
      openReleaseInfoMenu,
      openUserProfile,
      openArtistProfile,
      openBundleDetails,
      onToggleSlideInMenu,
      onCloseMenu,
      openMenu,
      releaseUuid,
      comicSlug,
      menuInstanceId,
      artistId,
      bundleId,
      username,
      navigationContext,
      goBack: historyLength > 0 ? goBack : undefined,
    }),
    [
      openComicInfoMenu,
      openReleaseInfoMenu,
      openUserProfile,
      openArtistProfile,
      openBundleDetails,
      onToggleSlideInMenu,
      onCloseMenu,
      openMenu,
      releaseUuid,
      comicSlug,
      menuInstanceId,
      artistId,
      bundleId,
      username,
      navigationContext,
      historyLength,
      goBack,
    ],
  );
}

export function getRelease(
  releaseUuid: string,
  navigationContext?: ReaderNavigationContext,
) {
  const contextQueryParams = new URLSearchParams();

  if (navigationContext) {
    Object.entries(navigationContext).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        contextQueryParams.append(
          `context_${key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)}`,
          value.toString(),
        );
      }
    });
  }

  return apiClient.get<ServerResponse<Release>>(
    `/v1/readV2/${releaseUuid}?releaseDetails=1${contextQueryParams.size > 0 ? "&" + contextQueryParams : ""}`,
  );
}

export function getComicReleases(comicSlug: string, langId?: string) {
  return apiClient.get<ServerListResponse<Release>>(
    `/v1/comics/${comicSlug}/releases?lang_id=${langId}`,
  );
}

export function getComic(
  comicSlug: string,
  navigationContext?: ReaderNavigationContext,
) {
  const contextQueryParams = new URLSearchParams();

  if (navigationContext) {
    Object.entries(navigationContext).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        contextQueryParams.append(
          `context_${key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)}`,
          value.toString(),
        );
      }
    });
  }
  return apiClient.get<ServerSingleResponse<Comic>>(
    `/v1/read/${comicSlug}${contextQueryParams.size > 0 ? "?" + contextQueryParams : ""}`,
  );
}

export function getArtist(artistId: number) {
  return apiClient.get<ServerSingleResponse<Artist>>(`/v1/artists/${artistId}`);
}

export function getUserProfile(
  username: string,
  navigationContext?: ReaderNavigationContext,
) {
  const contextQueryParams = new URLSearchParams();

  if (navigationContext) {
    Object.entries(navigationContext).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        contextQueryParams.append(
          `context_${key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)}`,
          value.toString(),
        );
      }
    });
  }

  return apiClient.get<ServerSingleResponse<User>>(
    `/v1/user-profiles/${username}${contextQueryParams.size > 0 ? "?" + contextQueryParams : ""}`,
  );
}

export function getBundleDetail(bundleId: string) {
  return apiClient.get<ServerSingleResponse<Bundle>>(`/v1/bundles/${bundleId}`);
}

export interface UseBundlePurchaseFunnelOptions {
  /**
   * Whether to use external modal (setFunnelModalConfig) or slide-in funnel (setFunnelConfig)
   * - true: External modal (for cards, external contexts)
   * - false: Slide-in funnel (for bundle details within slide-in menu)
   */
  useExternalModal?: boolean;

  /**
   * Custom setFunnelConfig function (for slide-in menu contexts)
   * If not provided and useExternalModal is false, will throw error
   */
  setFunnelConfig?: (
    config?: import("../../funnel/types").FunnelConfigTypes,
  ) => void;

  /**
   * Enable debug logging
   */
  debug?: boolean;
}

export function unlockBundle(
  bundleUuid: string,
  payload: BundlePurchasePayload,
) {
  return apiClient.post<ServerSingleResponse<unknown>>(
    `/v1/bundles/${bundleUuid}/unlock`,
    { meta: {}, payload: payload },
  );
}
